// ============================================
// Jeton JWT pour l’API Express GS (audience = app SPA)
// ============================================
//
// Les scopes par défaut incluent User.Read → l’access token a souvent
// audience Microsoft Graph. Notre serveur valide jwt avec audience =
// AZURE_AD_CLIENT_ID (l’app SPA). Il faut envoyer l’id_token (aud = clientId),
// pas l’access token Graph — sinon 401 partout.
// ============================================

import type {
  AccountInfo,
  AuthenticationResult,
  IPublicClientApplication,
  SilentRequest,
} from '@azure/msal-browser';

const MS_GRAPH_GUID = '00000003-0000-0000-c000-000000000000';

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = (4 - (b64.length % 4)) % 4;
    b64 += '='.repeat(pad);
    return JSON.parse(atob(b64)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function isMicrosoftGraphAudience(aud: string): boolean {
  if (aud === MS_GRAPH_GUID) return true;
  return aud.toLowerCase().startsWith('https://graph.microsoft.com');
}

/**
 * Choisit un JWT que notre backend peut vérifier (audience ≠ Graph).
 * Priorité : id_token (OIDC), puis access token si son `aud` est le client SPA.
 */
export function selectJwtForOwnBackend(
  result: AuthenticationResult,
  spaClientId: string
): string | null {
  const cid = spaClientId.toLowerCase();
  const ordered: string[] = [];
  if (result.idToken?.trim()) ordered.push(result.idToken.trim());
  if (result.accessToken?.trim()) ordered.push(result.accessToken.trim());

  for (const token of ordered) {
    const payload = decodeJwtPayload(token);
    const audRaw = payload?.aud;
    if (audRaw === undefined || audRaw === null) continue;
    const audStr = Array.isArray(audRaw) ? String(audRaw[0]) : String(audRaw);
    if (isMicrosoftGraphAudience(audStr)) continue;
    const audLower = audStr.toLowerCase();
    if (audLower === cid) return token;
    if (audLower.startsWith('api://') && audLower.includes(cid)) return token;
  }

  if (result.idToken?.trim()) return result.idToken.trim();
  return null;
}

/**
 * Obtient un JWT pour `Authorization: Bearer` vers l’API GS (même audience que le serveur).
 */
export async function acquireJwtForOwnBackend(
  instance: IPublicClientApplication,
  account: AccountInfo,
  scopes: string[],
  spaClientId: string
): Promise<string> {
  if (!spaClientId) {
    throw new Error('spaClientId manquant — vérifiez VITE_AZURE_AD_CLIENT_ID');
  }

  let result = await instance.acquireTokenSilent({ scopes, account } as SilentRequest);
  let picked = selectJwtForOwnBackend(result, spaClientId);
  if (picked) return picked;

  result = await instance.acquireTokenSilent({
    scopes: ['openid', 'profile', 'email'],
    account,
    forceRefresh: true,
  } as SilentRequest);
  picked = selectJwtForOwnBackend(result, spaClientId);
  if (picked) return picked;

  throw new Error(
    'Jeton API indisponible (id_token absent et access_token réservé à Graph). Essayez « Se déconnecter » puis reconnectez-vous.'
  );
}
